import { useState, useEffect } from "react";
import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";

const useOrderCalculations = (
  pricingPackage,
  isDiscountApplied,
  MultiplyWarranty,
  warrantyState,
  formattedString,
  upsellProduct
) => {
  const [calculations, setCalculations] = useState(null);
  useEffect(() => {
    // static values not dependend to bundle design mode or coupon code
    const oldPrice = pricingPackage?.old_price;
    const productAmount = pricingPackage?.amount;
    const oldPriceUpsell = Number(upsellProduct?.product_list_unit_old_price) * Number(pricingPackage?.amount);
    const upsellPrice = Number(upsellProduct?.product_list_unit_price) * Number(pricingPackage?.amount);

    //   calculate product price

    const price = upsellProduct
      ? Number(pricingPackage?.price) * Number(pricingPackage?.amount) + upsellPrice
      : Number(pricingPackage?.price) * Number(pricingPackage?.amount);

    //  calculate promo price

    let promoPrice = 0;

    if (isDiscountApplied) {
      promoPrice = upsellProduct
        ? Number(pricingPackage?.promo_discounted_price) * Number(pricingPackage?.amount) + upsellPrice
        : Number(pricingPackage?.promo_discounted_price) * Number(pricingPackage?.amount);
    }

    //   check if discount code is applied

    const productPrice = isDiscountApplied ? promoPrice : price;

    //   warranty price

    const warrantyPrice = !MultiplyWarranty
      ? upsellProduct
        ? Number(pricingPackage?.warranty_price) * 2
        : Number(pricingPackage?.warranty_price)
      : upsellProduct
        ? Number(pricingPackage?.warranty_price) * (Number(productAmount) + Number(productAmount))
        : Number(pricingPackage?.warranty_price) * Number(productAmount);
    const warranty = warrantyState ? warrantyPrice : 0;

    //   subtotal

    let subtotal = Number(productPrice) + Number(warranty);
    subtotal = subtotal.toFixed(2);

    //   shipping price

    let shippingPrice = isDiscountApplied
      ? Number(pricingPackage?.promo_shipping_price)
      : Number(pricingPackage?.shipping_price);
    shippingPrice = shippingPrice.toFixed(2);

    //   total

    let total = Number(subtotal) + Number(shippingPrice);
    total = total.toFixed(2);

    //   total savings

    let shippingDifference = 0;

    if (isDiscountApplied) {
      shippingDifference = Number(pricingPackage?.shipping_price) - Number(pricingPackage?.promo_shipping_price);
    }

    let totalSavings = upsellProduct
      ? Number(oldPrice) + oldPriceUpsell - Number(productPrice) + shippingDifference
      : Number(oldPrice) - Number(productPrice) + shippingDifference;
    totalSavings = totalSavings.toFixed(2);

    // format all amounts using formatted string and return

    setCalculations({
      savings: formattedString ? formattedString?.replace(/(0\.00|0,00|0)/, totalSavings) : "0.00",
      subtotal: formattedString ? formattedString?.replace(/(0\.00|0,00|0)/, subtotal) : "0.00",
      total: formattedString ? formattedString?.replace(/(0\.00|0,00|0)/, total) : "0.00",
      shipping: formattedString ? formattedString?.replace(/(0\.00|0,00|0)/, shippingPrice) : "0.00",
    });
  }, [pricingPackage, formattedString, warrantyState, isDiscountApplied, upsellProduct]);

  return calculations;
};

// Calculations for Order summary product card

const useCalculateProductCardPrice = (product, amount) => {
  const cartData = useCartData();
  const productData = useProductData();
  const [price, setPrice] = useState(null);
  const [promoPrice, setPromoPrice] = useState(null);
  const [oldPrice, setOldPrice] = useState(null);

  useEffect(() => {
    let warrantyPrice = !productData?.multiply_price_by_amount
      ? Number(product.warranty_price)
      : Number(product.warranty_price) * Number(product.amount);

    if (amount && warrantyPrice) {
      const splittedByAmount = warrantyPrice / product?.amount;
      warrantyPrice = splittedByAmount * amount;
    }

    const warranty = cartData?.warrantyState ? warrantyPrice : 0;

    // calculate product price

    const priceToFormat = (
      Number(product?.price) * Number(amount ? amount : product?.amount) +
      Number(warranty)
    ).toFixed(2);

    setPrice(priceToFormat);

    //  calculate promo price

    const promoPriceWithWarranty = (
      Number(product.promo_discounted_price) * Number(amount ? amount : product.amount) +
      Number(warranty)
    ).toFixed(2);
    setPromoPrice(promoPriceWithWarranty);

    // Calculate old price
    const oldPriceCalculated = (
      Number(amount ? (product.old_price / product.amount) * amount : product.old_price) + Number(warranty)
    ).toFixed(2);

    const formattedOldPrice = productData?.formatted_string?.replace(/(0\.00|0,00|0)/, oldPriceCalculated);
    setOldPrice(formattedOldPrice);
  }, [product, cartData?.warrantyState, cartData?.variants, cartData?.discountCode, amount]);

  return { oldPrice, price, promoPrice };
};

// Calculations for Order summary upsell card

const useCalculateUpsellCardPrice = (upsellProduct, selectedProduct) => {
  const cartData = useCartData();
  const productData = useProductData();
  const [upsellPrice, setUpsellPrice] = useState(null);
  const [upsellOldPrice, setUpsellOldPrice] = useState(null);
  const [oldPrice, setOldPrice] = useState(null);
  const product = selectedProduct;
  const amount = selectedProduct?.amount;

  useEffect(() => {
    let warrantyPrice = !productData?.multiply_price_by_amount
      ? Number(product.warranty_price)
      : Number(product.warranty_price) * Number(product.amount);

    if (amount && warrantyPrice) {
      const splittedByAmount = warrantyPrice / product?.amount;
      warrantyPrice = splittedByAmount * amount;
    }

    const warranty = cartData?.warrantyState ? warrantyPrice : 0;

    // calculate upsell price

    if (upsellProduct) {
      const upsellPrice = (Number(upsellProduct?.product_list_unit_price) * Number(amount) + Number(warranty)).toFixed(
        2
      );
      setUpsellPrice(upsellPrice);
    }

    // Calculate upsell old price
    if (upsellProduct) {
      const upsellOldPriceCalculated = Number(upsellProduct?.product_list_unit_old_price) * Number(amount).toFixed(2);
      if (cartData?.warrantyState) {
        setUpsellOldPrice(upsellOldPriceCalculated + Number(warranty));
      } else {
        setUpsellOldPrice(upsellOldPriceCalculated);
      }
    }

    const formattedOldPrice = productData?.formatted_string?.replace(/(0\.00|0,00|0)/, upsellOldPrice);
    setOldPrice(formattedOldPrice);
  }, [product, cartData?.warrantyState, cartData?.discountCode, amount, oldPrice, upsellPrice]);

  return { upsellPrice, oldPrice };
};

export { useOrderCalculations, useCalculateProductCardPrice, useCalculateUpsellCardPrice };
